import React from 'react'
import {
  Button,
  Form,
  Input,
  Select,
  Switch
} from 'antd'
import { LoginOutlined } from '@ant-design/icons'
import InputMask from 'react-input-mask'
import { sellerApi } from 'api/seller'
import { ECheckSellerDataTypes, ESellerLegalTypes, ISellerCreateAuthForm } from 'models/seller'
import { AuthContentWrapper } from 'components/layout/AuthContentWrapper/AuthContentWrapper'
import { IAlertData } from 'models/app'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { ruNamePattern } from 'shared/utils/patterns'
import { authApi } from 'api/auth'
import { InfoAlert } from '../InfoAlert/InfoAlert'
import styles from './SellerInfo.module.scss'
import { getSubmitData } from './utils/getSubmitData'

interface ISellerInfoProps {
  token: string
  onSuccess: () => void
  onGoBack?: () => void
}

export const SellerInfo: React.FC<ISellerInfoProps> = ({
  token,
  onSuccess,
  onGoBack
}) => {
  const [form] = Form.useForm<ISellerCreateAuthForm>()

  const [isLoading, setIsLoading] = React.useState(false)

  const [alertData, setAlertData] = React.useState<IAlertData<ECheckSellerDataTypes>>({
    visible: false,
    type: null
  })

  const [isNeedLegalChecking, setIsNeedLegalChecking] = React.useState(true)

  const legalType: ESellerLegalTypes | undefined = Form.useWatch('legalType', {
    form
  })
  const middleName: string | undefined = Form.useWatch('middleName', { form })
  const noMiddleName: boolean | undefined = Form.useWatch('noMiddleName', {
    form
  })

  const isMiddleNameLength: boolean = Boolean(middleName?.length ?? false)

  const legalINN: string =
    Form.useWatch('legalINN', { form, preserve: true }) ?? ''

  // У ИП ИНН из 12 цифр, у юрлица — из 10
  const innLength: number =
    legalType === ESellerLegalTypes.INDIVIDUAL ? 12 : 10

  const closeAlert = React.useCallback(() => {
    setAlertData({ visible: false, type: null })
  }, [])

  React.useEffect(() => {
    setIsNeedLegalChecking(true)
    closeAlert()
  }, [legalINN])

  React.useEffect(() => {
    if (legalType) {
      closeAlert()
      form.resetFields(['legalINN'])
    }
  }, [legalType])

  React.useEffect(() => {
    if (noMiddleName) {
      form.setFields([
        {
          name: 'middleName',
          errors: [],
          value: undefined
        }
      ])
    }
  }, [noMiddleName])

  React.useEffect(() => {
    if (isMiddleNameLength) {
      form.setFieldValue('noMiddleName', false)
      form.setFields([
        {
          name: 'middleName',
          errors: []
        }
      ])
    }
  }, [isMiddleNameLength])


  const checkLegalINN = async () => {
    try {
      await form.validateFields()
      const data: ISellerCreateAuthForm = form.getFieldsValue()
      setIsLoading(true)
      await sellerApi.checkLegalINN({ INN: data.legalINN }, token)
      setIsNeedLegalChecking(false)
      setAlertData({ visible: true, type: ECheckSellerDataTypes.SUCCESS })
    } catch (e: any) {
      if (!e.errorFields) {
        setAlertData({
          visible: true,
          type: ECheckSellerDataTypes.ERROR_LEGAL_INN
        })
        form.setFields([
          {
            name: 'legalINN',
            errors: ['']
          }
        ])
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = async (data: ISellerCreateAuthForm) => {
    try {
      setIsLoading(true)
      const normalizedData = getSubmitData(data)
      await authApi.createSeller(token, normalizedData)
      onSuccess()
      showMessage(
        'success',
        'Ваша заявка принята',
        'С вами свяжется менеджер для подтверждения данных'
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthContentWrapper
      width={764}
      title='Информация о продавце'
      titleLevel={2}
      titleOffset={30}
      titleClass={styles.title}
      padding={legalType ? '40px 56px 57px' : '34px 56px 16px'}
      onGoBack={onGoBack}
    >
      <Form form={form} name='sellerInfo' size='large' onFinish={onSubmit}>
        <Form.Item
          name='legalType'
          label='Выберите тип лица'
          rules={[
            {
              required: true,
              message: 'Выберите тип лица'
            }
          ]}
          rootClassName={styles.sellerType}
          hasFeedback
        >
          <Select
            placeholder='Выберите тип лица'
            options={[
              {
                value: ESellerLegalTypes.INDIVIDUAL,
                label: 'Индивидуальный предприниматель'
              },
              { value: ESellerLegalTypes.LEGAL, label: 'Юридическое лицо' }
            ]}
          />
        </Form.Item>
        <InfoAlert
          isVisible={alertData.visible}
          type={alertData.type}
          onClose={closeAlert}
        />
        {legalType && (
          <>
            <Form.Item
              name='firstName'
              label='Имя'
              rules={[
                {
                  required: true,
                  message: 'Заполните имя'
                },
                {
                  whitespace: true,
                  message: 'Заполните имя'
                },
                ruNamePattern
              ]}
              hasFeedback
            >
              <Input placeholder='Иван' />
            </Form.Item>
            <Form.Item
              name='lastName'
              label='Фамилия'
              rules={[
                {
                  required: true,
                  message: 'Заполните фамилию'
                },
                {
                  whitespace: true,
                  message: 'Заполните фамилию'
                },
                ruNamePattern
              ]}
              hasFeedback
            >
              <Input placeholder='Иванов' />
            </Form.Item>
            <div className={styles.middleName}>
              <Form.Item
                name='middleName'
                label='Отчество'
                rules={
                  noMiddleName
                    ? []
                    : [
                      {
                        required: true,
                        message: 'Заполните отчество'
                      },
                      {
                        whitespace: true,
                        message: 'Заполните отчество'
                      },
                      ruNamePattern
                    ]
                }
                style={{ flexGrow: 1 }}
                hasFeedback
              >
                <Input placeholder='Иванович' />
              </Form.Item>
              <Form.Item
                name='noMiddleName'
                label='Нет отчества'
                valuePropName='checked'
              >
                <Switch />
              </Form.Item>
            </div>
            <Form.Item
              name='legalINN'
              label='ИНН'
              rules={[
                {
                  required: true,
                  message: 'Заполните ИНН'
                },
                {
                  min: innLength,
                  message: 'Заполните ИНН корректно'
                }
              ]}
              hasFeedback
            >
              <InputMask
                mask={'9'.repeat(innLength)}
                maskChar={null}
              >
                {/* @ts-ignore */}
                {() => (
                  <Input
                    placeholder='Введите ИНН'
                    maxLength={innLength}
                  />
                )}
              </InputMask>
            </Form.Item>
            <Form.Item
              name='phone'
              label='Номер телефона'
              rules={[
                {
                  required: true,
                  message: 'Заполните номер телефона'
                },
                {
                  min: 18,
                  message: 'Заполните номер телефона'
                }
              ]}
              hasFeedback
            >
              <InputMask mask='+7 (999) 999-99-99' maskChar={null}>
                {/* @ts-ignore */}
                {() => <Input placeholder='Введите номер телефона' />}
              </InputMask>
            </Form.Item>
            <Form.Item name='edoOperator' label='Оператор ЭДО'>
              <Input placeholder='Введите оператора' />
            </Form.Item>
            <Form.Item name='edoId' label='ID ЭДО'>
              <Input placeholder='Введите идентификационный номер' />
            </Form.Item>
            <Form.Item
              name='shopName'
              label='Название магазина'
              rules={[
                {
                  required: true,
                  message: 'Заполните название магазина'
                },
                {
                  whitespace: true,
                  message: 'Заполните название магазина'
                }
              ]}
              hasFeedback
            >
              <Input placeholder='Введите название' />
            </Form.Item>
            <Form.Item
              name='legalAddress'
              label={
                legalType === ESellerLegalTypes.INDIVIDUAL
                  ? 'Адрес регистрации'
                  : 'Юридический адрес'
              }
              rules={[
                {
                  required: true,
                  message: 'Заполните адрес'
                },
                {
                  whitespace: true,
                  message: 'Заполните адрес'
                }
              ]}
              hasFeedback
            >
              <Input placeholder='Введите адрес' />
            </Form.Item>
            <Form.Item
              name='actualAddress'
              label='Фактический адрес'
              rules={[
                {
                  required: true,
                  message: 'Заполните фактический адрес'
                },
                {
                  whitespace: true,
                  message: 'Заполните фактический адрес'
                }
              ]}
              hasFeedback
            >
              <Input placeholder='Введите фактический адрес' />
            </Form.Item>
            {isNeedLegalChecking ? (
              <Button
                block
                htmlType='button'
                type='primary'
                size='large'
                icon={<LoginOutlined />}
                loading={isLoading}
                onClick={checkLegalINN}
              >
                Проверить
              </Button>
            ) : (
              <Button
                block
                htmlType='submit'
                type='primary'
                size='large'
                icon={<LoginOutlined />}
                loading={isLoading}
              >
                Отправить заявку
              </Button>
            )}
          </>
        )}
      </Form>
    </AuthContentWrapper>
  )
}
